import { Artist } from './ClientArtistSearch';

interface PopularityData {
  spotifyPopularity?: number; // 0-100
  lastFmListeners?: number;
  setlistFrequency?: number;
  combinedScore: number; // Final 0-100 score
}

interface PopularityCache {
  [artistName: string]: {
    data: PopularityData;
    timestamp: number;
  };
}

export class ArtistPopularityService {
  private static instance: ArtistPopularityService;
  private popularityCache: PopularityCache = {};
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  private constructor() {}

  static getInstance(): ArtistPopularityService {
    if (!ArtistPopularityService.instance) {
      ArtistPopularityService.instance = new ArtistPopularityService();
    }
    return ArtistPopularityService.instance;
  }

  async getPopularityScore(artist: Artist): Promise<number> {
    const normalizedName = this.normalizeArtistName(artist.name);
    const cached = this.getCachedPopularity(normalizedName);
    
    if (cached) {
      return cached.combinedScore;
    }

    const popularityData = await this.fetchPopularityData(artist);
    this.cachePopularityData(normalizedName, popularityData);
    
    return popularityData.combinedScore;
  }

  async getPopularityScores(artists: Artist[]): Promise<Map<string, number>> {
    const scores = new Map<string, number>();
    
    // Process in batches to avoid overwhelming APIs
    const batchSize = 10;
    for (let i = 0; i < artists.length; i += batchSize) {
      const batch = artists.slice(i, i + batchSize);
      const batchPromises = batch.map(async artist => {
        const score = await this.getPopularityScore(artist);
        return { name: artist.name, score };
      });
      
      const batchResults = await Promise.allSettled(batchPromises);
      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          scores.set(result.value.name, result.value.score);
        }
      });
    }
    
    return scores;
  }

  private async fetchPopularityData(artist: Artist): Promise<PopularityData> {
    const results = await Promise.allSettled([
      this.fetchSpotifyPopularity(artist),
      this.fetchLastFmPopularity(artist),
      this.fetchSetlistFrequency(artist)
    ]);

    const spotifyPopularity = results[0].status === 'fulfilled' ? results[0].value : undefined;
    const lastFmListeners = results[1].status === 'fulfilled' ? results[1].value : undefined;
    const setlistFrequency = results[2].status === 'fulfilled' ? results[2].value : undefined;

    const combinedScore = this.calculateCombinedScore({
      spotifyPopularity,
      lastFmListeners,
      setlistFrequency
    }, artist);

    return {
      spotifyPopularity,
      lastFmListeners,
      setlistFrequency,
      combinedScore
    };
  }

  private async fetchSpotifyPopularity(artist: Artist): Promise<number | undefined> {
    // Spotify Web API requires client credentials - disabled for now
    return undefined;
  }

  private async fetchLastFmPopularity(artist: Artist): Promise<number | undefined> {
    // Last.fm API requires API key - disabled for now
    return undefined;
  }

  private async fetchSetlistFrequency(artist: Artist): Promise<number | undefined> {
    // Setlist frequency API endpoint doesn't exist - disabled for now
    return undefined;
  }

  private calculateCombinedScore(data: Partial<PopularityData>, artist?: Artist): number {
    let score = 0;
    let sources = 0;

    // Spotify popularity (0-100) - highest weight
    if (data.spotifyPopularity !== undefined) {
      score += data.spotifyPopularity * 0.5;
      sources++;
    }

    // Last.fm listeners (normalize to 0-100) - medium weight
    if (data.lastFmListeners !== undefined) {
      // Normalize listener count to 0-100 scale (log scale)
      const normalizedListeners = Math.min(Math.log10(data.lastFmListeners) * 10, 100);
      score += normalizedListeners * 0.3;
      sources++;
    }

    // Setlist frequency - lower weight but still valuable
    if (data.setlistFrequency !== undefined) {
      // Normalize frequency to 0-100 scale
      const normalizedFrequency = Math.min(data.setlistFrequency / 10, 100);
      score += normalizedFrequency * 0.2;
      sources++;
    }

    // If no external data sources available, use intelligent fallback
    if (sources === 0) {
      return this.getFallbackPopularityScore(artist);
    }

    // Average the weighted scores
    return Math.round(score / sources);
  }

  private getFallbackPopularityScore(artist?: Artist): number {
    if (!artist) return 50;

    // Use MusicBrainz search score as popularity proxy when available
    if (artist.searchScore && artist.searchScore > 0) {
      // Convert MusicBrainz search score (0-100) to popularity score
      return Math.round(artist.searchScore);
    }

    // Built-in popularity database for major artists
    const popularityDatabase: { [key: string]: number } = {
      // Global superstars (90-100)
      'taylor swift': 100, 'drake': 98, 'the weeknd': 97, 'ariana grande': 96,
      'billie eilish': 95, 'dua lipa': 94, 'ed sheeran': 93, 'justin bieber': 92,
      'bad bunny': 91, 'the beatles': 90,
      
      // Major artists (70-89)
      'radiohead': 85, 'coldplay': 84, 'arctic monkeys': 82, 'queen': 89,
      'pink floyd': 88, 'led zeppelin': 87, 'the rolling stones': 86,
      'kendrick lamar': 83, 'kanye west': 81, 'beyoncé': 85,
      
      // Alternative/Indie favorites (60-79)
      'tame impala': 78, 'glass animals': 76, 'phoebe bridgers': 72,
      'vampire weekend': 74, 'the strokes': 79, 'mac miller': 71,
      'frank ocean': 75, 'tyler the creator': 73, 'sza': 77,
      
      // Cult classics and influential artists (50-69)
      'stereolab': 68, 'pixies': 67, 'sonic youth': 65, 'my bloody valentine': 64,
      'kraftwerk': 66, 'aphex twin': 63, 'boards of canada': 62,
      'neutral milk hotel': 61, 'animal collective': 60, 'death grips': 59,
      
      // Legendary artists with enduring popularity (80-90)
      'david bowie': 89, 'prince': 88, 'nirvana': 87, 'bob dylan': 86,
      'johnny cash': 83, 'elvis presley': 85, 'michael jackson': 92
    };

    const normalizedName = this.normalizeArtistName(artist.name);
    
    // Check popularity database
    if (popularityDatabase[normalizedName]) {
      return popularityDatabase[normalizedName];
    }

    // For unknown artists, return moderate score
    return 50;
  }

  private getCachedPopularity(normalizedName: string): PopularityData | null {
    const cached = this.popularityCache[normalizedName];
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      delete this.popularityCache[normalizedName];
      return null;
    }
    
    return cached.data;
  }

  private cachePopularityData(normalizedName: string, data: PopularityData): void {
    this.popularityCache[normalizedName] = {
      data,
      timestamp: Date.now()
    };
  }

  private normalizeArtistName(name: string): string {
    return name.toLowerCase().trim().replace(/^the\s+/i, '');
  }

  // Public method to clear cache if needed
  clearCache(): void {
    this.popularityCache = {};
  }

  // Public method to get cache statistics
  getCacheStats(): { size: number; oldestEntry: number | null } {
    const entries = Object.values(this.popularityCache);
    return {
      size: entries.length,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : null
    };
  }
}